/**
 * Environmental Impact — per-federation FEMA disaster exposure and the engagement
 * gap it correlates with. The health-plan value story: which federated cohorts sit
 * in disaster-burdened counties, and whether that exposure tracks lower engagement
 * (an actionable, population-specific signal for outreach prioritisation).
 *
 * Source: GET /api/legacy-trust-analytics/federation/environmental-impact
 * (legacy_trust.out_env_federation_impact). Internal CBO only (backend 403s otherwise).
 */

import React from 'react';
import { Flame, AlertTriangle, TrendingDown, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useEnvFederationImpact } from '@/hooks/ml';

// Decimal → JSON arrives as strings; coerce before any .toFixed()/Math.round().
const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};
const pct = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : `${n.toFixed(1)}%`;
};

export const EnvironmentalImpactCard: React.FC = () => {
  const { data, isLoading, isError } = useEnvFederationImpact();
  const rows = data?.federations ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Loading environmental impact…
        </CardContent>
      </Card>
    );
  }
  if (isError || !rows.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Environmental impact is available to the internal Trust Marketplace workspace only.
        </CardContent>
      </Card>
    );
  }

  // Headline = largest positive engagement gap (disaster-county members engage less).
  const gaps = rows
    .map((r) => ({ code: r.federation_code, gap: num(r.engagement_gap_pct) }))
    .filter((g): g is { code: string; gap: number } => g.gap != null)
    .sort((a, b) => b.gap - a.gap);
  const headline = gaps[0]?.gap > 0 ? gaps[0] : null;

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          FEMA disaster exposure (declared counties, 2018–present) by federated cohort,
          with the engagement gap between members in disaster-declared counties vs. others.
          A positive gap means disaster-county members engage less — a population-specific
          signal for outreach.
        </p>

        {headline && (
          <div className="flex items-center gap-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
            <TrendingDown className="h-4 w-4 shrink-0" />
            <span>
              <strong>{headline.code}</strong> members in disaster-declared counties engage{' '}
              <strong>{headline.gap.toFixed(1)} pts lower</strong> than members elsewhere.
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Federation</th>
                <th className="py-2 pr-3 text-right font-medium">Members</th>
                <th className="py-2 pr-3 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> In disaster county
                  </span>
                </th>
                <th className="py-2 pr-3 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    <Flame className="h-3 w-3" /> In fire county
                  </span>
                </th>
                <th className="py-2 pr-3 text-right font-medium">Engaged · disaster</th>
                <th className="py-2 pr-3 text-right font-medium">Engaged · other</th>
                <th className="py-2 text-right font-medium">Gap</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const gap = num(r.engagement_gap_pct);
                return (
                  <tr key={r.federation_code} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-medium">{r.federation_code}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.members)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmt(r.in_disaster_county)}{' '}
                      <span className="text-muted-foreground">({pct(r.pct_disaster_county)})</span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.in_fire_county)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{pct(r.engaged_pct_disaster)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{pct(r.engaged_pct_other)}</td>
                    <td
                      className={`py-2 text-right font-medium tabular-nums ${
                        gap == null ? 'text-muted-foreground' : gap > 0 ? 'text-rose-600' : 'text-emerald-600'
                      }`}
                    >
                      {gap == null ? '—' : `${gap > 0 ? '+' : ''}${gap.toFixed(1)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
